import React from 'react';
import {IconArrowMergeRight, IconArrowsSplit, IconDots} from '@tabler/icons-react';
import type {NexusNode} from 'src/api/ProactApiClient';
import { NexusNodeType, NexusVisualState, TaskTypeEnum} from 'src/api/ProactApiClient';
import {workflowEntityConfig} from 'src/app/workflows/workflowDesignSystem';
import {PathUtility} from 'src/shared/constants/PathBuilder';

// --- Layout constants ---

export const NODE_WIDTH = 280;
export const NODE_HEIGHT = 110;

// --- Node type config ---

interface NodeTypeConfigEntry {
  icon: React.ReactNode;
  color: string;
  label: string;
  getDisplayName: (node: NexusNode) => string;
  getSubtitle: (node: NexusNode) => string | null;
  getUrl: (node: NexusNode) => string | null;
}

export const nodeTypeConfig: Record<NexusNodeType, NodeTypeConfigEntry> = {
  [NexusNodeType.Workflow]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Workflow].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Workflow].color,
    label: workflowEntityConfig[NexusNodeType.Workflow].label,
    getDisplayName: (node) => node.definitionName ?? 'Workflow',
    getSubtitle: (node) => node.internalIdentifier ?? null,
    getUrl: (node) =>
      node.workflowDefinitionId && node.workflowId
        ? PathUtility.WorkflowView(String(node.workflowDefinitionId), String(node.workflowId))
        : null,
  },
  [NexusNodeType.Task]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Task].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Task].color,
    label: workflowEntityConfig[NexusNodeType.Task].label,
    getDisplayName: (node) => node.subject ?? 'Task',
    getSubtitle: (node) => node.assigneeName ?? null,
    getUrl: (node) => {
      if (!node.taskType || !node.taskDefinitionId) return null;
      const taskTypeMap: Record<string, TaskTypeEnum> = {
        Actions: TaskTypeEnum.Actions,
        Approval: TaskTypeEnum.Approval,
        ToolboxTalk: TaskTypeEnum.ToolboxTalk,
        Form: TaskTypeEnum.Form,
      };
      const taskType = taskTypeMap[node.taskType];
      if (taskType) return PathUtility.TaskView(taskType, String(node.taskDefinitionId), 'content');
      return null;
    },
  },
  [NexusNodeType.Form]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Form].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Form].color,
    label: workflowEntityConfig[NexusNodeType.Form].label,
    getDisplayName: (node) => node.name ?? 'Form',
    getSubtitle: (node) => {
      const parts = [node.submittedByName, node.internalIdentifier?.abbreviate(12)].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    },
    getUrl: (node) =>
      node.formDefinitionId && node.formId
        ? PathUtility.FormSubmission(
            String(node.formDefinitionId),
            String(node.formId),
            'view' as never
          )
        : null,
  },
  [NexusNodeType.Email]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Email].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Email].color,
    label: workflowEntityConfig[NexusNodeType.Email].label,
    getDisplayName: (node) => node.subject ?? 'Email',
    getSubtitle: (node) => (node.recipientCount ? `${node.recipientCount} recipient(s)` : null),
    getUrl: () => null,
  },
  [NexusNodeType.TrainingSession]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.TrainingSession].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.TrainingSession].color,
    label: workflowEntityConfig[NexusNodeType.TrainingSession].label,
    getDisplayName: (node) => node.courseName ?? 'Training Session',
    getSubtitle: (node) => {
      const parts = [node.traineeName, node.internalIdentifier?.abbreviate(12)].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    },
    getUrl: (node) =>
      node.courseDefinitionId && node.courseAssignmentId && node.courseSessionId
        ? PathUtility.TrainingCourseSessionView(
            String(node.courseDefinitionId),
            String(node.courseAssignmentId),
            String(node.courseSessionId),
            'overview' as never
          )
        : null,
  },
  [NexusNodeType.ItemAssignment]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.ItemAssignment].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.ItemAssignment].color,
    label: workflowEntityConfig[NexusNodeType.ItemAssignment].label,
    getDisplayName: (node) => node.itemName ?? 'Item',
    getSubtitle: (node) => node.assigneeName ?? null,
    getUrl: (node) =>
      node.itemDefinitionId && node.itemAssignmentId
        ? PathUtility.ItemAssignmentView(String(node.itemDefinitionId), String(node.itemAssignmentId))
        : null,
  },
  [NexusNodeType.Schedule]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Schedule].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Schedule].color,
    label: workflowEntityConfig[NexusNodeType.Schedule].label,
    getDisplayName: (node) => `Schedule (${node.intervalType ?? 'Unknown'})`,
    getSubtitle: (node) =>
      node.nextProcessDate ? `Next: ${new Date(node.nextProcessDate).toLocaleDateString()}` : null,
    getUrl: () => null,
  },
  [NexusNodeType.BadgeExtension]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.BadgeExtension].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.BadgeExtension].color,
    label: workflowEntityConfig[NexusNodeType.BadgeExtension].label,
    getDisplayName: (node) =>
      node.badgeExtensionDays ? `Extend Badge (+${node.badgeExtensionDays}d)` : 'Extend Badge',
    getSubtitle: (node) => (node.badgeExtensionDays ? `${node.badgeExtensionDays} days` : null),
    getUrl: () => null,
  },
  [NexusNodeType.Unknown]: {
    icon: React.createElement(workflowEntityConfig[NexusNodeType.Unknown].icon, {size: 16}),
    color: workflowEntityConfig[NexusNodeType.Unknown].color,
    label: workflowEntityConfig[NexusNodeType.Unknown].label,
    getDisplayName: () => 'Manual / Unknown Trigger',
    getSubtitle: (node) => node.triggeredByName ?? null,
    getUrl: () => null,
  },
};

// --- Visual state styling ---

export function getNodeStyle(visualState: NexusVisualState): React.CSSProperties {
  switch (visualState) {
    case NexusVisualState.Active:
      return {
        borderLeft: '3px solid var(--mantine-color-blue-5)',
      };
    case NexusVisualState.Done:
      return {
        borderLeft: '3px solid var(--mantine-color-green-5)',
      };
    case NexusVisualState.Muted:
      return {
        borderLeft: '3px solid var(--mantine-color-gray-4)',
        opacity: 0.5,
      };
    case NexusVisualState.Cancelled:
      return {
        borderLeft: '3px solid var(--mantine-color-orange-5)',
      };
  }
}

// --- State legend items ---

export const stateLegendItems: {label: string; color: string}[] = [
  {label: 'Active', color: 'var(--mantine-color-blue-5)'},
  {label: 'Completed', color: 'var(--mantine-color-green-5)'},
  {label: 'Cancelled', color: 'var(--mantine-color-orange-5)'},
  {label: 'Not Started', color: 'var(--mantine-color-gray-4)'},
];

export const nodeTypeLegendItems: {label: string; icon: React.ReactNode; dashed?: boolean}[] = [
  {label: 'Phase (all complete)', icon: React.createElement(IconArrowMergeRight, {size: 14, color: 'var(--mantine-color-gray-6)'})},
  {label: 'Phase (first complete)', icon: React.createElement(IconArrowsSplit, {size: 14, color: 'var(--mantine-color-gray-6)'})},
  {label: 'Depth limit', icon: React.createElement(IconDots, {size: 14, color: 'var(--mantine-color-gray-6)'}), dashed: true},
];
